import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Select, MenuItem, Switch,
  Button, Alert, CircularProgress, Chip, Snackbar, FormControlLabel,
  InputLabel, FormControl, Divider,
} from '@mui/material';
import { httpClient } from '../services/http-client';
import { JevSettingsCard } from '../components/JevSettingsCard';

interface Provider {
  slot: number;
  provider: string;
  model: string;
  enabled: boolean;
  key_last4: string;
  has_key: boolean;
  /** Endpoint for the custom provider; ignored for the built-in ones. */
  base_url?: string;
}

const PROVIDERS = [
  { value: 'nvidia', label: 'NVIDIA NIM', hint: 'Free tier available. OpenAI-compatible.' },
  { value: 'deepseek', label: 'DeepSeek', hint: 'Low cost, pay as you go.' },
  { value: 'openai', label: 'OpenAI', hint: '' },
  { value: 'anthropic', label: 'Anthropic', hint: '' },
  { value: 'openrouter', label: 'OpenRouter', hint: 'Routes to many models behind one key.' },
  { value: 'custom', label: 'Custom (OpenAI-compatible)', hint: '' },
];

const SLOTS = [1, 2, 3];

/**
 * Bring-your-own inference credentials.
 *
 * THREE SLOTS, TRIED IN ORDER. Free and low-cost endpoints fail often — rate
 * limits, model deprecations, cold starts — and a single provider means
 * analysis is simply unavailable when it happens. An ordered fallback turns an
 * outage into a slower answer.
 *
 * A stored key is never shown again, only its last four characters. Leaving
 * the key field blank keeps whatever is saved, so editing the model does not
 * force you to re-enter a secret this page cannot display.
 */
export const LLMSettingsPage: React.FC = () => {
  const [rows, setRows] = useState<Record<number, Provider>>({});
  const [keys, setKeys] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paywalled, setPaywalled] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    httpClient
      .get<{ providers: Provider[] }>('/settings/llm')
      .then((r) => {
        const list = ((r as any)?.data?.providers ?? (r as any)?.providers ?? []) as Provider[];
        const map: Record<number, Provider> = {};
        list.forEach((p) => { map[p.slot] = p; });
        setRows(map);
        setError(null);
        setPaywalled(false);
      })
      .catch((e: any) => {
        // 402 is the plan gate, not a failure. Saying "upgrade" is useful;
        // saying "error" would send someone hunting for a bug.
        if (String(e?.message ?? '').toLowerCase().includes('paid')) setPaywalled(true);
        else setError(e?.message ?? 'Could not load providers');
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async (slot: number) => {
    const cur = rows[slot];
    setSaving(slot);
    try {
      await httpClient.put(`/settings/llm/${slot}`, {
        provider: cur?.provider || 'nvidia',
        model: cur?.model || '',
        api_key: keys[slot] || '',
        base_url: cur?.base_url || '',
        enabled: cur?.enabled ?? true,
      });
      setKeys((k) => ({ ...k, [slot]: '' }));
      load();
      setToast(`Slot ${slot} saved`);
    } catch (e: any) {
      setToast(e?.message ?? 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const remove = async (slot: number) => {
    setSaving(slot);
    try {
      await httpClient.delete(`/settings/llm/${slot}`);
      load();
      setToast(`Slot ${slot} cleared`);
    } catch (e: any) {
      setToast(e?.message ?? 'Delete failed');
    } finally {
      setSaving(null);
    }
  };

  const patch = (slot: number, p: Partial<Provider>) =>
    setRows((r) => ({ ...r, [slot]: { ...(r[slot] ?? { slot } as Provider), ...p } }));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 820 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>AI analysis</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Bring your own inference key. Slots are tried in order, so a rate limit
        or an outage on the first falls through to the next rather than leaving
        analysis unavailable.
      </Typography>

      {paywalled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          AI analysis is available on the paid plan. Anything you configure here
          is kept if you upgrade later.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Alert severity="success" variant="outlined" sx={{ mb: 3 }}>
        Keys are encrypted before they are stored, with the same cipher that
        protects your bank tokens. Once saved, a key is never shown again —
        only its last four characters — so it cannot be read back out of this
        page.
      </Alert>

      <Stack spacing={2}>
        {SLOTS.map((slot) => {
          const r = rows[slot];
          const meta = PROVIDERS.find((p) => p.value === (r?.provider ?? 'nvidia'));
          return (
            <Paper key={slot} sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  {slot === 1 ? 'Primary' : slot === 2 ? 'Fallback' : 'Last resort'}
                </Typography>
                {r?.has_key && (
                  <Chip size="small" variant="outlined" sx={{ height: 20 }}
                    label={`key ••••${r.key_last4}`} />
                )}
                <Box sx={{ flex: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={r?.enabled ?? true}
                      onChange={(e) => patch(slot, { enabled: e.target.checked })}
                    />
                  }
                  label={<Typography variant="caption">Enabled</Typography>}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl size="small" sx={{ minWidth: 210 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    label="Provider"
                    value={r?.provider ?? 'nvidia'}
                    onChange={(e) => patch(slot, { provider: e.target.value })}
                  >
                    {PROVIDERS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Model"
                  placeholder="e.g. deepseek-chat"
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off', 'data-1p-ignore': true, 'data-lpignore': true }}
                  value={r?.model ?? ''}
                  onChange={(e) => patch(slot, { model: e.target.value })}
                  sx={{ minWidth: 220 }}
                />

                {r?.provider === 'custom' && (
                  <TextField
                    size="small"
                    label="Base URL"
                    placeholder="https://your-endpoint/v1"
                    autoComplete="off"
                    value={r?.base_url ?? ''}
                    onChange={(e) => patch(slot, { base_url: e.target.value })}
                    helperText="Required for a custom provider."
                    sx={{ minWidth: 260 }}
                  />
                )}

                <TextField
                  size="small"
                  label={r?.has_key ? 'Replace key (optional)' : 'API key'}
                  type="password"
                  autoComplete="new-password"
                  inputProps={{ autoComplete: 'new-password', 'data-1p-ignore': true, 'data-lpignore': true }}
                  value={keys[slot] ?? ''}
                  onChange={(e) => setKeys((k) => ({ ...k, [slot]: e.target.value }))}
                  helperText={r?.has_key ? 'Leave blank to keep the stored key' : ' '}
                  sx={{ flex: 1, minWidth: 240 }}
                />
              </Stack>

              {meta?.hint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {meta.hint}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small" variant="contained"
                  disabled={saving === slot}
                  onClick={() => save(slot)}
                >
                  Save
                </Button>
                {r?.has_key && (
                  <Button
                    size="small" color="error"
                    disabled={saving === slot}
                    onClick={() => remove(slot)}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* Separate from the inference slots: Jev has its own key and only
          categorises transactions, so it is not part of the analysis chain. */}
      <Box sx={{ mt: 4 }}>
        <JevSettingsCard />
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
};
